import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";

import {
  clearFlowReassignmentPreview,
  startExecuteFlowReassignment,
  startPreviewFlowReassignment
} from "../../app/actions/flowReassignmentActions";
import { startLoadingCatalogModules } from "../../app/actions/catalogModuleActions";
import { startLoadingCatalogRoles } from "../../app/actions/catalogRoleActions";
import FlowEngineGlobalGate from "../specialist-flow-engine/FlowEngineGlobalGate";

const SPECIALIST_ROLE_NAMES = [
  "Specialist-Manager",
  "Specialist-Pre-Manager",
  "Specialist-Validator",
  "Specialist-Editor"
];

const AdminFlowReassignmentScreen = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { loading, preview, executeResult } = useSelector((s) => s.flowReassignment);
  const { catalogModules = [] } = useSelector((s) => s.catalogModule);
  const { catalogRoles = [] } = useSelector((s) => s.catalogRole);

  const [filters, setFilters] = useState({
    catalogmodule_id: "",
    catalogrole_id: "",
    stale_user_id: "",
    only_misaligned: true,
    limit_per_module: ""
  });

  useEffect(() => {
    dispatch(startLoadingCatalogModules());
    dispatch(startLoadingCatalogRoles());
    return () => dispatch(clearFlowReassignmentPreview());
  }, [dispatch]);

  const specialistRoles = useMemo(
    () => (catalogRoles || []).filter(
      (role) => !role.deleted && SPECIALIST_ROLE_NAMES.includes(role.name)
    ),
    [catalogRoles]
  );

  const apiFilters = useMemo(() => ({
    catalogmodule_id: filters.catalogmodule_id ? Number(filters.catalogmodule_id) : undefined,
    catalogrole_id: filters.catalogrole_id ? Number(filters.catalogrole_id) : undefined,
    stale_user_id: filters.stale_user_id ? Number(filters.stale_user_id) : undefined,
    only_misaligned: filters.only_misaligned,
    limit_per_module: filters.limit_per_module ? Number(filters.limit_per_module) : undefined
  }), [filters]);

  const flatItems = useMemo(() => {
    if (!preview?.modules) {
      return [];
    }
    return preview.modules.flatMap((moduleBlock) =>
      (moduleBlock.items || []).map((item) => ({
        ...item,
        process: moduleBlock.process,
        catalogmodule_id: moduleBlock.catalogmodule_id
      }))
    );
  }, [preview]);

  const runPreview = async () => {
    await dispatch(startPreviewFlowReassignment(apiFilters));
  };

  const runExecute = async () => {
    const eligible = preview?.summary?.records_eligible ?? 0;
    if (!eligible) {
      Swal.fire(t("titles.warning"), t("admin.flowReassignment.noEligible"), "warning");
      return;
    }

    const confirm = await Swal.fire({
      title: t("admin.flowReassignment.confirmTitle"),
      html: t("admin.flowReassignment.confirmText", { count: eligible }),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("admin.flowReassignment.confirmButton"),
      cancelButtonText: t("buttons.cancel")
    });

    if (!confirm.isConfirmed) {
      return;
    }

    const result = await dispatch(startExecuteFlowReassignment(apiFilters));
    if (result) {
      await dispatch(startPreviewFlowReassignment(apiFilters));
    }
  };

  return (
    <FlowEngineGlobalGate>
      <div className="container-fluid py-3">
        <h2>{t("admin.flowReassignment.title")}</h2>
        <p className="text-muted">{t("admin.flowReassignment.subtitle")}</p>

        <div className="alert alert-info">
          <strong>{t("admin.flowReassignment.algorithmsTitle")}</strong>
          <ul className="mb-0 mt-2">
            <li>{t("admin.flowReassignment.algorithmManual")}</li>
            <li>{t("admin.flowReassignment.algorithmBalanced")}</li>
            <li>{t("admin.flowReassignment.algorithmRoundRobin")}</li>
          </ul>
          <small className="d-block mt-2">{t("admin.flowReassignment.algorithmConfigHint")}</small>
        </div>

        <div className="card mb-4">
          <div className="card-body">
            <h5 className="card-title">{t("admin.flowReassignment.filtersTitle")}</h5>
            <div className="row g-2">
              <div className="col-md-3">
                <label className="form-label">{t("admin.flowModule.module")}</label>
                <select
                  className="form-select"
                  value={filters.catalogmodule_id}
                  onChange={(e) => setFilters({ ...filters, catalogmodule_id: e.target.value })}
                >
                  <option value="">{t("admin.flowModule.allModules")}</option>
                  {(catalogModules || []).filter((m) => !m.deleted).map((m) => (
                    <option key={m.id} value={m.id}>{m.name} ({m.key || m.process})</option>
                  ))}
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label">{t("admin.flowReassignment.roleFilter")}</label>
                <select
                  className="form-select"
                  value={filters.catalogrole_id}
                  onChange={(e) => setFilters({ ...filters, catalogrole_id: e.target.value })}
                >
                  <option value="">{t("admin.flowReassignment.allRoles")}</option>
                  {specialistRoles.map((role) => (
                    <option key={role.id} value={role.id}>{role.name}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-2">
                <label className="form-label">{t("admin.flowReassignment.staleUser")}</label>
                <input
                  type="number"
                  className="form-control"
                  min="1"
                  placeholder="7"
                  value={filters.stale_user_id}
                  onChange={(e) => setFilters({ ...filters, stale_user_id: e.target.value })}
                />
              </div>
              <div className="col-md-2">
                <label className="form-label">{t("admin.flowReassignment.limit")}</label>
                <input
                  type="number"
                  className="form-control"
                  min="1"
                  value={filters.limit_per_module}
                  onChange={(e) => setFilters({ ...filters, limit_per_module: e.target.value })}
                />
              </div>
              <div className="col-md-2 d-flex align-items-end">
                <div className="form-check mb-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="onlyMisaligned"
                    checked={filters.only_misaligned}
                    onChange={(e) => setFilters({ ...filters, only_misaligned: e.target.checked })}
                  />
                  <label className="form-check-label" htmlFor="onlyMisaligned">
                    {t("admin.flowReassignment.onlyMisaligned")}
                  </label>
                </div>
              </div>
            </div>
            <div className="mt-3 d-flex gap-2">
              <button type="button" className="btn btn-primary" disabled={loading} onClick={runPreview}>
                {loading ? t("labels.loading", "Cargando...") : t("admin.flowReassignment.previewButton")}
              </button>
              <button type="button" className="btn btn-warning" disabled={loading || !preview} onClick={runExecute}>
                {t("admin.flowReassignment.executeButton")}
              </button>
            </div>
          </div>
        </div>

        {preview && (
          <div className="card mb-4">
            <div className="card-body">
              <h5 className="card-title">{t("admin.flowReassignment.summaryTitle")}</h5>
              <p className="mb-1">
                {t("admin.flowReassignment.summaryScanned")}: <strong>{preview.summary?.records_scanned ?? 0}</strong>
              </p>
              <p className="mb-3">
                {t("admin.flowReassignment.summaryEligible")}: <strong>{preview.summary?.records_eligible ?? 0}</strong>
              </p>

              {flatItems.length === 0 ? (
                <p className="text-muted mb-0">{t("admin.flowReassignment.emptyPreview")}</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm table-striped">
                    <thead>
                      <tr>
                        <th>{t("admin.flowReassignment.colModule")}</th>
                        <th>{t("admin.flowReassignment.colRecord")}</th>
                        <th>{t("admin.flowReassignment.colRole")}</th>
                        <th>{t("admin.flowReassignment.colCurrent")}</th>
                        <th>{t("admin.flowReassignment.colExpected")}</th>
                        <th>{t("admin.flowReassignment.colReason")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {flatItems.map((item) => (
                        <tr key={`${item.process}-${item.record_id}`}>
                          <td>{item.process}</td>
                          <td>{item.record_name || item.record_id}</td>
                          <td>{item.current_role_name}</td>
                          <td>{item.current_user_id ?? "—"}</td>
                          <td>{item.expected_user_id ?? "—"}</td>
                          <td>{item.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {executeResult && (
          <div className="alert alert-success">
            {t("admin.flowReassignment.lastExecute", {
              updated: executeResult.summary?.records_updated ?? 0,
              batch: executeResult.batch_id || "—"
            })}
          </div>
        )}
      </div>
    </FlowEngineGlobalGate>
  );
};

export default AdminFlowReassignmentScreen;
